import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';

export const SUPPLIER_VAT_RULES: Readonly<Record<string, { pattern: RegExp; hint: string; maxLength: number }>> = {
  AT: { pattern: /^U[0-9]{8}$/, hint: 'U seguita da 8 cifre', maxLength: 9 },
  BE: { pattern: /^[01][0-9]{9}$/, hint: '10 cifre, prima cifra 0 o 1', maxLength: 10 },
  BG: { pattern: /^[0-9]{9,10}$/, hint: '9 o 10 cifre', maxLength: 10 },
  CY: { pattern: /^[0-9]{8}[A-Z]$/, hint: '8 cifre e una lettera maiuscola', maxLength: 9 },
  HR: { pattern: /^[0-9]{11}$/, hint: '11 cifre', maxLength: 11 },
  DK: { pattern: /^[0-9]{8}$/, hint: '8 cifre', maxLength: 8 },
  EE: { pattern: /^[0-9]{9}$/, hint: '9 cifre', maxLength: 9 },
  FI: { pattern: /^[0-9]{8}$/, hint: '8 cifre', maxLength: 8 },
  FR: { pattern: /^[0-9A-HJ-NP-Z]{2}[0-9]{9}$/, hint: '2 caratteri alfanumerici (escluse I e O) e 9 cifre', maxLength: 11 },
  DE: { pattern: /^[0-9]{9}$/, hint: '9 cifre', maxLength: 9 },
  EL: { pattern: /^[0-9]{9}$/, hint: '9 cifre', maxLength: 9 },
  IE: { pattern: /^(?:[0-9]{7}[A-W][A-IW]?|[0-9][A-Z+*][0-9]{5}[A-W])$/, hint: '7 cifre e 1-2 lettere; ammesso anche il formato storico irlandese', maxLength: 9 },
  IT: { pattern: /^[0-9]{11}$/, hint: '11 cifre numeriche', maxLength: 11 },
  LV: { pattern: /^[0-9]{11}$/, hint: '11 cifre', maxLength: 11 },
  LT: { pattern: /^(?:[0-9]{9}|[0-9]{12})$/, hint: '9 o 12 cifre', maxLength: 12 },
  LU: { pattern: /^[0-9]{8}$/, hint: '8 cifre', maxLength: 8 },
  MT: { pattern: /^[0-9]{8}$/, hint: '8 cifre', maxLength: 8 },
  NL: { pattern: /^[0-9]{9}B[0-9]{2}$/, hint: '9 cifre, B e 2 cifre', maxLength: 12 },
  PL: { pattern: /^[0-9]{10}$/, hint: '10 cifre', maxLength: 10 },
  PT: { pattern: /^[0-9]{9}$/, hint: '9 cifre', maxLength: 9 },
  CZ: { pattern: /^[0-9]{8,10}$/, hint: '8, 9 o 10 cifre', maxLength: 10 },
  RO: { pattern: /^[0-9]{2,10}$/, hint: 'Da 2 a 10 cifre', maxLength: 10 },
  SK: { pattern: /^[0-9]{10}$/, hint: '10 cifre', maxLength: 10 },
  SI: { pattern: /^[0-9]{8}$/, hint: '8 cifre', maxLength: 8 },
  ES: { pattern: /^(?:[A-Z][0-9]{7}[A-Z0-9]|[0-9]{8}[A-Z])$/, hint: '9 caratteri: lettera, 7 cifre e alfanumerico, oppure 8 cifre e lettera', maxLength: 9 },
  SE: { pattern: /^[0-9]{10}01$/, hint: '12 cifre, ultime due 01', maxLength: 12 },
  HU: { pattern: /^[0-9]{8}$/, hint: '8 cifre', maxLength: 8 },
  CHE: { pattern: /^(?:[0-9]{9}|[0-9]{3}\.[0-9]{3}\.[0-9]{3})(?: ?(?:MWST|TVA|IVA))?$/, hint: '9 cifre, punti e suffisso MWST/TVA/IVA facoltativi', maxLength: 16 },
  GB: { pattern: /^(?:[0-9]{9}|[0-9]{12})$/, hint: '9 o 12 cifre', maxLength: 12 },
};

/**
 * Service con validatori comuni per i form dei fornitori
 */
@Injectable({
  providedIn: 'root'
})
export class SupplierValidatorsService {
  
  /**
   * Il prefisso paese e' memorizzato separatamente dal numero.
   */
  static vatNumberValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    const country = control.parent?.get('vatCountry')?.value;
    const rule = Object.hasOwn(SUPPLIER_VAT_RULES, country) ? SUPPLIER_VAT_RULES[country] : undefined;
    const value = control.value.toString();
    return rule?.pattern.exec(value)?.[0] === value
      ? null
      : { invalidVatNumber: true };
  }

  static vatCountryValidator(control: AbstractControl): ValidationErrors | null {
    return Object.hasOwn(SUPPLIER_VAT_RULES, control.value)
      ? null
      : { invalidVatCountry: true };
  }

  static vatNumberHint(country: string): string {
    return SUPPLIER_VAT_RULES[country]?.hint ?? 'Seleziona un paese IVA valido';
  }

  static vatNumberMaxLength(country: string): number {
    return SUPPLIER_VAT_RULES[country]?.maxLength ?? 30;
  }

  /**
   * Validatore per Codice Fiscale
   * Formato: 16 caratteri alfanumerici (persone fisiche) o 11 cifre (persone giuridiche)
   */
  static taxCodeValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    const taxCode = control.value.toString().toUpperCase().replace(/\s/g, '');
    const taxCodeRegex = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$|^[0-9]{11}$/;
    return taxCodeRegex.test(taxCode) ? null : { invalidTaxCode: true };
  }

  /**
   * Validatore per PEC (Posta Elettronica Certificata)
   * Formato: email valida
   */
  static pecEmailValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(control.value) ? null : { invalidPecEmail: true };
  }

  /**
   * Validatore per numero di telefono italiano
   * Formato: +39 seguito da 8-11 cifre, oppure 8-11 cifre senza prefisso
   */
  static phoneNumberValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    const phoneNumber = control.value.toString().replace(/\s/g, '');
    const phoneRegex = /^(\+39)?[0-9]{8,11}$/;
    return phoneRegex.test(phoneNumber) ? null : { invalidPhoneNumber: true };
  }

  /**
   * Validatore per numero di cellulare italiano
   * Formato: +39 seguito da 3 e altre 9 cifre, oppure 3 e altre 9 cifre
   */
  static mobileNumberValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    const mobileNumber = control.value.toString().replace(/\s/g, '');
    const mobileRegex = /^(\+39)?3[0-9]{9}$/;
    return mobileRegex.test(mobileNumber) ? null : { invalidMobileNumber: true };
  }
}
